class FreeShippingMeter extends HTMLElement {
    constructor() {
        super();
        this._lastTotal = null;
        this._lastText = null;
        this._lastProgress = null;
    }

    static freeShippingText = window.free_shipping_text?.free_shipping_message;
    static freeshipPrice = window.free_shipping_price !== undefined ? parseFloat(window.free_shipping_price) : 99;
    static thresholdPercent = parseFloat(window.free_shipping_threshold || 0);

    connectedCallback() {
        this.freeShippingEligible = 0;
        this.progressBar = this.querySelector('[data-shipping-progress]');
        this.messageElement = this.querySelector('[data-shipping-message]');
        this.textEnabled = this.progressBar?.dataset.textEnabled === 'true';
        this.progressMeter = this.querySelector('[data-free-shipping-progress-meter]');

        this.onCartChangeHandler = this.onCartChange.bind(this);

        this.addEventListener('change', this.onCartChangeHandler);

        // Passive global event listeners
        const opts = { passive: true };
        document.addEventListener('cart:updated', this.onCartChangeHandler, opts);
        document.addEventListener('cart-update', this.onCartChangeHandler, opts);
        document.addEventListener('cart:change', this.onCartChangeHandler, opts);
        document.addEventListener('cart:refresh', this.onCartChangeHandler, opts);
        document.addEventListener('discount:applied', this.onCartChangeHandler, opts);
        document.addEventListener('discount-updated', this.onCartChangeHandler, opts);

        this.initialize();
    }

    disconnectedCallback() {
        if (this.onCartChangeHandler) {
            this.removeEventListener('change', this.onCartChangeHandler);
            document.removeEventListener('cart:updated', this.onCartChangeHandler);
            document.removeEventListener('cart-update', this.onCartChangeHandler);
            document.removeEventListener('cart:change', this.onCartChangeHandler);
            document.removeEventListener('cart:refresh', this.onCartChangeHandler);
            document.removeEventListener('discount:applied', this.onCartChangeHandler);
            document.removeEventListener('discount-updated', this.onCartChangeHandler);
        }
    }

    _isBopisItem(item) {
        if (!item || !item.properties) return false;
        const props = item.properties;
        const check = (rawK, rawV) => {
            const k = String(rawK || '').toLowerCase().trim();
            const v = String(rawV || '').toLowerCase().trim();
            if (k === '_pickupstore' || k === '_pickup_store' || k === 'pickup store' ||
                k === '_shopifylocationid' || k === '_shopify_location_id' ||
                k === '_deliverymethod' || k === '_delivery_method' || k === 'delivery method' ||
                k === 'delivery_type' || k === '_delivery_type' || k === 'bopis' || k === '_bopis') {
                return true;
            }
            if (k.includes('pickup') || k.includes('bopis') || k.includes('delivery method')) {
                return true;
            }
            if (v.includes('pick up at store') || v.includes('pickup at store') || v.includes('pick up in store') || v.includes('store pickup') || v.includes('store-')) {
                return true;
            }
            return false;
        };

        if (Array.isArray(props)) {
            return props.some(p => check(p.name || p.first || p[0], p.value || p.last || p[1]));
        }
        if (typeof props === 'object') {
            return Object.entries(props).some(([k, v]) => check(k, v));
        }
        return false;
    }

    initialize(cartData) {
        if (cartData && typeof cartData.total_price !== 'undefined' && Array.isArray(cartData.items)) {
            this.cart = cartData;
            this.calculateProgress(cartData);
            return;
        }

        // Guard: never allow more than one in-flight fetch, or more than one per 500ms 
        if (this._fetchInFlight) return;
        if (this._lastFetchAt && Date.now() - this._lastFetchAt < 500) return;
        this._lastFetchAt = Date.now();
        this._fetchInFlight = true;

        fetch((window.Shopify?.routes?.root || '/') + 'cart.js?v=' + Date.now())
            .then(res => res.json())
            .then(cart => {
                this.cart = cart;
                this.calculateProgress(cart);
            })
            .catch(() => {
                if (window.Shopify && Shopify.getCart) {
                    Shopify.getCart((cart) => {
                        this.cart = cart;
                        this.calculateProgress(cart);
                    });
                }
            })
            .finally(() => { this._fetchInFlight = false; });
    }

    onCartChange(e) {
        const cartData = e && e.detail ? (e.detail.cart || e.detail) : null;
        this.initialize(cartData);
    }

    calculateProgress(cart) {
        if (!cart || typeof cart.total_price === 'undefined') return;

        let hasShippableItems = false;
        let hasBopisItems = false;

        const items = Array.isArray(cart.items) ? cart.items : [];
        items.forEach(item => {
            if (this._isBopisItem(item)) {
                hasBopisItems = true;
            } else {
                hasShippableItems = true;
            }
        });

        // If cart has only BOPIS (store pickup) items, hide the shipping progress bar
        if (hasBopisItems && !hasShippableItems) {
            if (this.style.display !== 'none') this.style.display = 'none';
            return;
        }

        // Use standard cart total (no exclusion)
        const total = parseFloat((parseInt(cart.total_price, 10) / 100).toFixed(2));

        // Skip DOM manipulation if shippable total hasn't changed
        if (this._lastTotal === total) return;
        this._lastTotal = total;

        const targetPrice = window.free_shipping_price !== undefined
            ? parseFloat(window.free_shipping_price)
            : (FreeShippingMeter.freeshipPrice !== undefined ? FreeShippingMeter.freeshipPrice : 99);

        // Check visibility threshold
        const thresholdAmount = targetPrice * (FreeShippingMeter.thresholdPercent / 100);
        if (total < thresholdAmount) {
            if (this.style.display !== 'none') this.style.display = 'none';
            return;
        } else {
            if (this.style.display !== 'block') this.style.display = 'block';
        }

        let freeShipBar = 0;
        if (targetPrice <= 0 || total >= targetPrice) {
            freeShipBar = 100;
        } else {
            freeShipBar = Math.min(100, Math.max(0, (total / targetPrice) * 100));
        }

        const text = this.getText(total, targetPrice);
        this.setProgressWidthAndText(freeShipBar, text);
    }

getText(total, targetPrice) {
    if (targetPrice <= 0 || total >= targetPrice) {
        this.freeShippingEligible = 1;
        return `<b>FREE shipping</b>`;
    } else {
        const diff = (targetPrice - total).toFixed(2);
        return `<span>Spend <b>$${diff}</b> more to unlock — <b>FREE shipping</b></span>`;
    }
}

setProgressWidthAndText(progress, text) {
    // Prevent redundant DOM reflows
    if (this.messageElement && this._lastText !== text) {
        this._lastText = text;
        this.messageElement.innerHTML = text;
    }

    if (this.progressMeter && this._lastProgress !== progress) {
        this._lastProgress = progress;
        this.progressMeter.style.width = `${progress}%`;
    }

    if (progress >= 100) {
        if (!this.classList.contains('progress-free')) this.classList.add('progress-free');
    } else {
        if (this.classList.contains('progress-free')) this.classList.remove('progress-free');
    }
}
}

if (typeof customElements !== 'undefined' && !customElements.get('free-shipping-component')) {
    customElements.define('free-shipping-component', FreeShippingMeter);
}

// Debounce helper to coalesce rapid cart/discount events into a single update
let _freeShippingDebounceTimer = null;
window.updateFreeShippingMeter = function (cartData) {
    clearTimeout(_freeShippingDebounceTimer);
    _freeShippingDebounceTimer = setTimeout(() => {
        if (typeof document !== 'undefined') {
            document.querySelectorAll('free-shipping-component').forEach(el => {
                if (typeof el.initialize === 'function') {
                    el.initialize(cartData);
                }
            });
            if (typeof window.updateMobileStickyTotal === 'function') {
                window.updateMobileStickyTotal(cartData);
            }
        }
    }, 150);
};

// Targeted Network Interceptor for Fetch & XMLHttpRequest calls to /cart mutation endpoints
(function () {
    if (typeof window === 'undefined' || window._freeShippingFetchIntercepted || typeof window.fetch === 'undefined') return;
    window._freeShippingFetchIntercepted = true;

    const originalFetch = window.fetch;
    const cartMutationRegex = /\/cart\/(add|change|update|clear)(\.js|\b)/i;

    window.fetch = function () {
        const url = arguments[0];
        const urlString = typeof url === 'string' ? url : (url && url.url ? url.url : '');
        const fetchPromise = originalFetch.apply(this, arguments);

        // Only intercept cart mutation endpoints, NEVER read-only /cart.js
        if (cartMutationRegex.test(urlString) && !urlString.includes('/cart.js?')) {
            fetchPromise.then(res => {
                if (res && res.clone) {
                    res.clone().json().then(data => {
                        if (data && typeof data.total_price !== 'undefined') {
                            window.updateFreeShippingMeter(data);
                        }
                    }).catch(() => { });
                }
            }).catch(() => { });
        }
        return fetchPromise;
    };
})();

(function () {
    if (typeof window === 'undefined' || window._freeShippingXHRIntercepted || typeof XMLHttpRequest === 'undefined') return;
    window._freeShippingXHRIntercepted = true;

    const origOpen = XMLHttpRequest.prototype.open;
    const cartMutationRegex = /\/cart\/(add|change|update|clear)(\.js|\b)/i;

    XMLHttpRequest.prototype.open = function (method, url) {
        this._url = url;
        return origOpen.apply(this, arguments);
    };

    const origSend = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.send = function () {
        this.addEventListener('load', function () {
            if (typeof this._url === 'string' && cartMutationRegex.test(this._url) && !this._url.includes('/cart.js?')) {
                try {
                    const data = JSON.parse(this.responseText);
                    if (data && typeof data.total_price !== 'undefined') {
                        window.updateFreeShippingMeter(data);
                    }
                } catch (e) { }
            }
        });
        return origSend.apply(this, arguments);
    };
})();